import { useQuery } from '@tanstack/react-query';
import axios from '@/utils/api/axios';
import { IOrganizationItem } from '@/types/organization';
import { ILegalEntityItem } from '@/types/legal-entity';

export interface ILegalEntitySelectOption extends ILegalEntityItem {
  label: string;
}

/**
 * Custom hook to fetch legal entities for a specific organization
 * @param organizationId - The organization ID to fetch legal entities for
 * @returns Query result with legal entities data, loading state, and error handling
 */
export const useLegalEntities = (organizationId?: string) => {
  return useQuery({
    queryKey: ['legalEntities', organizationId?.toString()],
    queryFn: async () => {
      if (!organizationId) {
        return [];
      }

      const response = await axios.get<{ data: ILegalEntityItem[] }>('legal-entities/all', {
        params: { organization: organizationId },
      });

      return (
        (response.data.data?.map((entity) => ({
          ...entity,
          label: entity.legal_name,
          code: entity.tax_number || ' ',
        })) as ILegalEntitySelectOption[]) || []
      );
    },
    enabled: !!organizationId,
    refetchOnMount: 'always',
  });
};

/**
 * Hook variant that accepts organization object directly
 * @param currentOrganization - The organization object containing _id
 * @returns Query result with legal entities data, loading state, and error handling
 */
export const useLegalEntitiesForOrganization = (currentOrganization?: IOrganizationItem) => {
  return useLegalEntities(currentOrganization?._id);
};
